/**
 * Punkt 29 P2 (Antoine, freigegebenes Mockup 26.09.2026): der Zahlungsplan-Hinweis nach einer
 * Betragsänderung. Ein Kunde hat ein angenommenes Angebot geändert und ERNEUT angenommen — der
 * Zahlungsplan steht danach noch auf dem alten Betrag.
 *
 * WAS HIER PASSIERT: nur die Entscheidung, OB und WELCHER Hinweis erscheint. Gerechnet wird hier
 * nichts Neues: ob sich der Plan anpassen lässt, sagt `pruefeAnpassung` (dahinter `verteileNeu`,
 * dieselbe Funktion wie in der Route). Der Überschuss ist eine Subtraktion zweier Zahlen, die die
 * Seite ohnehin anzeigt (Bezahlt, Angebotssumme).
 *
 * KEINE NEUE SPALTE: alles aus Verlauf, Status und Zahlungszeilen, die die Vorgangsseite schon lädt.
 */
import { pruefeAnpassung, type AnpassungsZeile } from "./zahlungsanpassung-anzeige";
import { berechneZahlungsKennzahlen } from "./zahlungs-kennzahlen";

type HinweisAktivitaet = { action: string; createdAt: string; details?: Record<string, unknown> | null };
type HinweisZeile = AnpassungsZeile & { refundedAmountCents?: number; stripePaymentIntentId?: string | null };

export type ZahlungsplanHinweis =
  | { art: "anpassen"; angebotCents: number; planCents: number; bezahltVorhanden: boolean }
  | {
      art: "ueberschuss";
      angebotCents: number;
      bezahltCents: number;
      ueberschussCents: number;
      /** P3: die Stripe-Zahlung, über deren Teil-Erstattung der Überschuss zurückgeht (null = keine erstattbare). */
      erstatten: { paymentId: string; betragCents: number } | null;
    };

/**
 * „Der Kunde hat nach der Änderung wieder zugestimmt": es gibt ein „Für Änderung geöffnet", und der
 * Vorgang steht wieder auf einem Zusage-Status. Das Wiederöffnen setzt ihn auf `offer_sent`; zurück
 * kommt er nur über eine Annahme — öffentlich ODER telefonisch. Am Status statt an der Zeile
 * `offer.chosen` gemessen, weil der telefonische Weg diese Zeile nicht schreibt.
 */
const NACH_ZUSAGE = new Set(["offer_chosen", "payment_failed", "balance_due", "paid"]);

export function zahlungsplanHinweisNachAenderung(eingabe: {
  status: string;
  aktivitaeten: readonly HinweisAktivitaet[];
  amountTotalCents: number | null | undefined;
  payments: readonly HinweisZeile[];
}): ZahlungsplanHinweis | null {
  const { status, aktivitaeten, amountTotalCents, payments } = eingabe;
  if (!NACH_ZUSAGE.has(status)) return null;
  if (!aktivitaeten.some((a) => a.action === "offer.reopened_for_amendment")) return null;
  if (!amountTotalCents || amountTotalCents <= 0 || payments.length === 0) return null;

  const { paidCents } = berechneZahlungsKennzahlen([...payments], amountTotalCents);
  // Szenario C: der neue Betrag liegt unter dem Bezahlten. Umverteilen hilft nicht, nur Erstatten.
  if (paidCents > amountTotalCents) {
    const ueberschussCents = paidCents - amountTotalCents;
    // P3: keine Stornierung der bezahlten Anzahlungsrechnung; der Knopf öffnet nur die bestehende
    // Teil-Erstattung (`POST /api/payments/:id/refund`) — vorbelegt mit dem Überschuss, gedeckelt
    // auf das, was diese eine Zahlung noch hergibt. Die größte erstattbare Stripe-Zahlung zuerst.
    // ponytail: ein Überschuss über mehrere Zahlungen verteilt braucht mehrere Erstattungen von Hand.
    const kandidat = payments
      .filter((p) => p.status === "succeeded" && p.stripePaymentIntentId)
      .map((p) => ({ paymentId: p.id, nettoCents: p.amountCents - (p.refundedAmountCents ?? 0) }))
      .filter((k) => k.nettoCents > 0)
      .sort((a, b) => b.nettoCents - a.nettoCents)[0];
    const erstatten = kandidat ? { paymentId: kandidat.paymentId, betragCents: Math.min(ueberschussCents, kandidat.nettoCents) } : null;
    return { art: "ueberschuss", angebotCents: amountTotalCents, bezahltCents: paidCents, ueberschussCents, erstatten };
  }
  // Szenario A/B: nur wenn die bestehende Anpassung wirklich etwas täte. Passt der Plan, verschwindet
  // der Hinweis von selbst.
  if (!pruefeAnpassung(amountTotalCents, payments).ausfuehrbar) return null;
  // Dieselbe Lesart wie `verteileNeu`: storniert gehört nicht zum Plan.
  const planCents = payments.filter((p) => p.status !== "canceled").reduce((s, p) => s + p.amountCents, 0);
  return { art: "anpassen", angebotCents: amountTotalCents, planCents, bezahltVorhanden: payments.some((p) => p.status === "succeeded") };
}

/**
 * Offene Raten, deren Zahlungslink entwertet wurde (Plananpassung oder Betragsänderung, Zeile
 * `payment.link_invalidated`) und die noch keinen neuen haben. An ihnen steht „Neuen Zahlungslink
 * senden" — der Versand bleibt ein Klick des Betreibers, nie automatisch (Entscheidung 3).
 */
export function ratenOhneGueltigenLink<T extends HinweisZeile>(payments: readonly T[], aktivitaeten: readonly HinweisAktivitaet[]): T[] {
  const entwertet = new Set(
    aktivitaeten.filter((a) => a.action === "payment.link_invalidated").map((a) => a.details?.paymentId).filter((x): x is string => typeof x === "string"),
  );
  return payments.filter((p) => (p.status === "pending" || p.status === "processing") && !p.checkoutUrl && entwertet.has(p.id));
}
